import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import $ from 'jquery';
import { X, Save, Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { getCountryList, getStateListByCountryId, getCityListByStateId, bindSourceMediumCampaign, updateField, getCollaboratorTypes, getTeamsByTypeId, getUserListByUserId, fetchCountryFlagsAndCodes } from '../../utils/lead';
import { getSession } from '../../getSession';
import MediaLibrary from '../MediaLibrary';
import Swal from 'sweetalert2';

if (typeof window !== 'undefined') {
  window.jQuery = window.$ = $;
}

const EditLeadForm = React.forwardRef(({ lead, onClose, onSubmit }, ref) => {
  // Strip a dial-code prefix from a raw mobile string (e.g. "+91 9876543210" → "9876543210")
  const stripDialCode = (raw, code) => {
    const str = (raw || '').toString().trim();
    const prefix = (code || '').toString().trim();
    if (!str) return '';
    // If the number starts with the code (with or without space), strip it
    if (prefix && str.startsWith(prefix)) {
      return str.slice(prefix.length).replace(/^\s+/, '');
    }
    // Also handle generic leading + sign with digits
    const m = str.match(/^\+\d+\s+(.*)$/);
    if (m) return m[1].trim();
    return str;
  };

  const [formData, setFormData] = useState(() => {
    const session = getSession() || {};
    const sessionUserId = session.userId ?? session.UserId ?? '';
    const sessionParentId = session.parentId ?? session.ParentId ?? session.ParentID ?? '';
    const rawCode = lead?.CountryCode || '+91';
    const rawMobile = (lead?.MobileNo || '').split(',')[0];
    // Strip dial code from mobile so it isn't shown twice
    const cleanMobile = (() => {
      const str = rawMobile.toString().trim();
      if (rawCode && str.startsWith(rawCode)) return str.slice(rawCode.length).replace(/^\s+/, '');
      const m = str.match(/^\+\d+\s+(.*)$/);
      if (m) return m[1].trim();
      return str;
    })();

    return {
      name: lead?.PersonName || '',
      company: lead?.CompanyName || '',
      mobile: cleanMobile,
      countryCode: rawCode,
      email: (lead?.EmailID || '').split(',')[0].trim(),
      country: lead?.Country || '',
      countryId: lead?.CountryId || '',
      state: lead?.State || '',
      stateId: (lead?.StateId ?? lead?.StateID) || '',
      city: lead?.City || '',
      source: lead?.SourceName || '',
      sourceId: (lead?.SourceId ?? lead?.SourceID) || '',
      medium: lead?.MediumName || 'Web Form',
      mediumId: (lead?.MediumId ?? lead?.MediumID) || '',
      campaign: lead?.CampaignName || '',
      campaignId: (lead?.CampaignId ?? lead?.CampaignID) || '',
      resiAddress: lead?.ResidentialAddress || '',
      officeAddress: lead?.OfficialAddress || lead?.OfficeAddress || '',
      remark: lead?.InitialRemarks || lead?.Remarks || '',
      pinCode: lead?.PinCode || '0',
      tags: lead?.LeadTags || lead?.Tags || '',
      PersonImage: lead?.PhotoName || lead?.Photo || '',
      photoUrl: lead?.PhotoUrl || lead?.Photo_Url || '',
      userId: sessionUserId,
      parentId: sessionParentId
    };
  });

  // Sync formData when lead prop changes
  useEffect(() => {
    if (lead) {
      const rawCode = (lead.CountryCode || '').toString().trim() || '+91';
      const cleanMobile = (() => {
        const str = (lead.MobileNo || '').toString().split(',')[0].trim();
        const m = str.match(/^\+\d+\s+(.*)$/);
        if (m) return m[1].trim();
        return str;
      })();

      setFormData(prev => ({
        ...prev,
        name: lead?.PersonName || '',
        company: lead?.CompanyName || '',
        mobile: cleanMobile,
        countryCode: rawCode,
        email: (lead?.EmailID || '').split(',')[0].trim(),
        country: lead?.Country || '',
        countryId: lead?.CountryId || '',
        state: lead?.State || '',
        stateId: (lead?.StateId ?? lead?.StateID) || '',
        city: lead?.City || '',
        source: lead?.SourceName || '',
        sourceId: (lead?.SourceId ?? lead?.SourceID) || '',
        medium: lead?.MediumName || 'Web Form',
        mediumId: (lead?.MediumId ?? lead?.MediumID) || '',
        campaign: lead?.CampaignName || '',
        campaignId: (lead?.CampaignId ?? lead?.CampaignID) || '',
        resiAddress: lead?.ResidentialAddress || '',
        officeAddress: lead?.OfficialAddress || lead?.OfficeAddress || '',
        remark: lead?.InitialRemarks || lead?.Remarks || '',
        pinCode: lead?.PinCode || '0',
        tags: lead?.LeadTags || lead?.Tags || '',
        PersonImage: lead?.PhotoName || lead?.Photo || '',
        photoUrl: lead?.PhotoUrl || lead?.Photo_Url || '',
      }));
    }
  }, [lead]);

  const getLeadId = () => Number(lead?.LeadId ?? lead?.ID ?? lead?.Id ?? 0) || 0;
  const getLeadNo = () => String(lead?.LeadNo ?? lead?.LeadId ?? lead?.ID ?? lead?.Id ?? '0');

  const formatMobileWithCode = (mobile, code) => {
    const cleanMobile = (mobile || '').toString().trim();
    if (!cleanMobile) return '';
    const cleanCode = (code || '').toString().trim();
    return cleanCode ? `${cleanCode} ${cleanMobile}` : cleanMobile;
  };
  const session = getSession() || {};
  const sessionUserId = session.userId ?? session.UserId ?? '';
  const sessionParentId = session.parentId ?? session.ParentId ?? session.ParentID ?? '';


  // ─── collaborator state ───────────────────────────────────────────────────
  const [showCollaborator, setShowCollaborator] = useState(false);
  const [collabTypeOptions, setCollabTypeOptions] = useState([]);
  const [collabTeamOptions, setCollabTeamOptions] = useState([]);
  const [collabMode, setCollabMode] = useState('team');          // 'team' | 'user'
  const [userOptions, setUserOptions] = useState([]);

  // { id: '...', name: '...' }
  const [collabSelectedType, setCollabSelectedType] = useState({ id: '', name: '' });
  const [collabSelectedValue, setCollabSelectedValue] = useState({ id: '', name: '' });

  // Array of { typeId, typeName, mode, selectedId, selectedName }
  const [collaborators, setCollaborators] = useState([]);

  const typeIds = [];
  const teamIds = [];
  const userIds = [];

  if (showCollaborator) {
    collaborators.forEach((c) => {
      typeIds.push(String(c.typeId || 0));
      if (String(c.mode).toLowerCase() === 'team') {
        teamIds.push(String(c.selectedId || 0));
        userIds.push('0');
      } else {
        teamIds.push('0');
        userIds.push(String(c.selectedId || 0));
      }
    });
  }

  const buildLeadDetailsPayload = () => ({
    LeadId: getLeadId(),
    ID: getLeadId(),
    LeadNo: getLeadNo(),
    PersonName: formData.name || '',
    CompanyName: formData.company || '',
    MobileNo: formatMobileWithCode(formData.mobile, formData.countryCode),
    MobileNo1: '',
    MobileNo2: '',
    CountryCode: formData.countryCode || '',
    CountryCode1: '',
    CountryCode2: '',
    EmailID: formData.email || '',
    EmailID1: '',
    EmailID2: '',
    Country: formData.country || '',
    State: formData.state || '',
    City: formData.city || '',
    PinCode: formData.pinCode || '',
    ResidentialAddress: formData.resiAddress || '',
    OfficialAddress: formData.officeAddress || '',
    // prefer sending selected IDs (if available) for Source/Medium; fall back to text
    SourceName: (formData.sourceId ?? formData.source) || '',
    //SourceId: formData.sourceId || '',
    MediumName: (formData.mediumId ?? formData.medium) || '',
    //MediumId: formData.mediumId || '',
    CampaignName: (formData.campaignId ?? formData.campaign) || '',
    CampaignId: formData.campaignId || '',
    InitialRemarks: formData.remark || '',
    LeadTags: formData.tags || '',
    Remarks: formData.remark || '',
    Tags: formData.tags || '',
    CountryId: formData.countryId || '',
    StateId: formData.stateId || '',
    WhereAddress: lead?.WhereAddress || '',
    WhereLatitude: lead?.WhereLatitude || '',
    WhereLongitude: lead?.WhereLongitude || '',
    lstCustomField: Array.isArray(lead?.lstCustomField) ? lead.lstCustomField : [],
    userId: sessionUserId,
    parentId: sessionParentId,
    photoUrl: formData.photoUrl || '',
    TypeIds: showCollaborator ? typeIds.join(',') : '',
    TeamIds: showCollaborator ? teamIds.join(',') : '',
    UserIds: showCollaborator ? userIds.join(',') : '',
    DeletedTypeIds: '',
    DeletedTeamIds: '',
    DeletedUserIds: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For mobile keep countryCode separate via dropdown; just set number here
    if (name === 'mobile') {
      setFormData(prev => ({ ...prev, mobile: (value || '').toString() }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryCodeChange = (e) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, countryCode: val }));
  };

  // Media library modal state and handlers
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const openMediaLibrary = () => setShowMediaLibrary(true);
  const handleMediaSelect = (media) => {
    if (!media) return;
    setFormData(prev => ({ ...prev, photoName: media.name || '', photoUrl: media.url || media.thumbnail || '' }));
    setShowMediaLibrary(false);
  };

  const submitForm = () => {
    // Basic validations
    if ((!formData.sourceId && !formData.source) || (!formData.mediumId && !formData.medium)) {
      Swal.fire({ icon: 'warning', title: 'Required', text: 'Please select source and medium' });
      return;
    }

    // Name validation (alphanumeric, dot, underscore, space)
    if (formData.name) {
      const nameRegex = /^[a-zA-Z0-9._ ]+$/;
      if (!nameRegex.test(formData.name)) {
        Swal.fire({ icon: 'error', title: 'Invalid Name', text: 'Name can only contain letters, numbers, dots, and underscores.' });
        return;
      }
    }

    // Email validation
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        Swal.fire({ icon: 'error', title: 'Invalid Email', text: 'Please enter a valid email address' });
        return;
      }
    }

    // Mobile validation
    if (formData.mobile) {
      const mobileRegex = /^\d+$/;
      if (!mobileRegex.test(formData.mobile)) {
        Swal.fire({ icon: 'error', title: 'Invalid Mobile', text: 'Please enter a valid mobile number (digits only)' });
        return;
      }

      const code = formData.countryCode;
      const num = formData.mobile;
      if (code === '+91' && num.length !== 10) {
        Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 10 digits for India' });
        return;
      } else if (code === '+1' && num.length !== 10) {
        Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 10 digits for US' });
        return;
      } else if (code === '+44' && num.length !== 10) {
        Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 10 digits for UK' });
        return;
      } else if (code === '+971' && num.length !== 9) {
        Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 9 digits for UAE' });
        return;
      } else if (code === '+61' && num.length !== 9) {
        Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 9 digits for Australia' });
        return;
      } else if (num.length < 7 || num.length > 15) {
        Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Please enter a valid mobile number length' });
        return;
      }
    }

    if (typeof onSubmit === 'function') {
      onSubmit(buildLeadDetailsPayload());
    }
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submitForm();
  };

  useImperativeHandle(ref, () => ({
    submit: () => submitForm()
  }), [formData, lead, onSubmit]);

  const handleSaveField = async (fieldName) => {
    try {
      // Validations
      if (fieldName === 'name' && formData.name) {
        const nameRegex = /^[a-zA-Z0-9._ ]+$/;
        if (!nameRegex.test(formData.name)) {
          Swal.fire({ icon: 'error', title: 'Invalid Name', text: 'Name can only contain letters, numbers, dots, and underscores.' });
          return;
        }
      }
      if (fieldName === 'email' && formData.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          Swal.fire({ icon: 'error', title: 'Invalid Email', text: 'Please enter a valid email address' });
          return;
        }
      }
      if (fieldName === 'mobile' && formData.mobile) {
        const mobileRegex = /^\d+$/;
        if (!mobileRegex.test(formData.mobile)) {
          Swal.fire({ icon: 'error', title: 'Invalid Mobile', text: 'Please enter a valid mobile number (digits only)' });
          return;
        }

        const code = formData.countryCode;
        const num = formData.mobile;
        if (code === '+91' && num.length !== 10) {
          Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 10 digits for India' });
          return;
        } else if (code === '+1' && num.length !== 10) {
          Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 10 digits for US' });
          return;
        } else if (code === '+44' && num.length !== 10) {
          Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 10 digits for UK' });
          return;
        } else if (code === '+971' && num.length !== 9) {
          Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 9 digits for UAE' });
          return;
        } else if (code === '+61' && num.length !== 9) {
          Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Mobile number must be 9 digits for Australia' });
          return;
        } else if (num.length < 7 || num.length > 15) {
          Swal.fire({ icon: 'error', title: 'Invalid Mobile length', text: 'Please enter a valid mobile number length' });
          return;
        }
      }

      console.log(`Saving ${fieldName}:`, formData[fieldName]);
      const { TokenId, userId } = getSession();
      const leadId = lead?.ID || lead?.Id || 0;

      // Map local form field names to DB field names
      const fieldMap = {
        name: 'PersonName',
        company: 'CompanyName',
        mobile: 'MobileNo',
        email: 'EmailID',
        country: 'Country',
        state: 'State',
        city: 'City',
        pinCode: 'PinCode',
        tags: 'Tags',
        remark: 'Remarks'
      };

      const tableName = 'Lead';

      // Special handling for mobile: save CountryCode separately, then mobile number
      if (fieldName === 'mobile') {
        // save country code first
        const countryCodePayload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId || 0,
          MAC_Address: '',
          IP_Address: '102.16.32.189',
          Details: {
            FieldId: 0,
            FieldName: 'CountryCode',
            FieldValue: formData.countryCode || '',
            TableName: tableName,
            UserId: userId || 0,
            FilterExp: `LeadId=${leadId}`
          },
          BroadcastName: ''
        };

        await updateField(countryCodePayload);

        // then save mobile number
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId || 0,
          MAC_Address: '',
          IP_Address: '102.16.32.189',
          Details: {
            FieldId: 0,
            FieldName: fieldMap[fieldName] || fieldName,
            FieldValue: formatMobileWithCode(formData.mobile, formData.countryCode),
            TableName: tableName,
            UserId: userId || 0,
            FilterExp: `LeadId=${leadId}`
          },
          BroadcastName: ''
        };

        const resp = await updateField(payload);
        if (resp?.Status === 1 || (resp?.Details && Number(resp.Details) > 0)) {
          Swal.fire({ icon: 'success', title: 'Saved', text: 'Mobile saved successfully', timer: 1500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save mobile' });
        }
        return;
      }

      // Generic field save
      const mapped = fieldMap[fieldName] || fieldName;
      const payload = {
        Token: TokenId,
        Message: '',
        LoggedUserId: userId || 0,
        MAC_Address: '',
        IP_Address: '102.16.32.189',
        Details: {
          FieldId: 0,
          FieldName: mapped,
          FieldValue: formData[fieldName] || '',
          TableName: tableName,
          UserId: userId || 0,
          FilterExp: `LeadId=${leadId}`
        },
        BroadcastName: ''
      };

      const resp = await updateField(payload);
      if (resp?.Status === 1 || (resp?.Details && Number(resp.Details) > 0)) {
        Swal.fire({ icon: 'success', title: 'Saved', text: `${fieldName} saved successfully`, timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: `Failed to save ${fieldName}` });
      }
    } catch (err) {
      console.error('handleSaveField error', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Error saving field' });
    }
  };

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [mediumOptions, setMediumOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);

  // Resolve sourceId/mediumId/campaignId from options if they are missing but names are present
  useEffect(() => {
    if (sourceOptions.length > 0 && formData.source && !formData.sourceId) {
      const found = sourceOptions.find(opt => (opt.SrcmedCampName || opt.Name) === formData.source);
      if (found) {
        setFormData(prev => ({ ...prev, sourceId: String(found.SrcmedCampID || found.SrcmedCampId || found.SrcMedCampID || '') }));
      }
    }
  }, [sourceOptions, formData.source, formData.sourceId]);

  useEffect(() => {
    if (mediumOptions.length > 0 && formData.medium && !formData.mediumId) {
      const found = mediumOptions.find(opt => (opt.SrcmedCampName || opt.Name) === formData.medium);
      if (found) {
        setFormData(prev => ({ ...prev, mediumId: String(found.SrcmedCampID || found.SrcmedCampId || found.SrcMedCampID || '') }));
      }
    }
  }, [mediumOptions, formData.medium, formData.mediumId]);

  useEffect(() => {
    if (campaignOptions.length > 0 && formData.campaign && !formData.campaignId) {
      const found = campaignOptions.find(opt => (opt.SrcmedCampName || opt.Name) === formData.campaign);
      if (found) {
        setFormData(prev => ({ ...prev, campaignId: String(found.SrcmedCampID || found.SrcmedCampId || found.SrcMedCampID || '') }));
      }
    }
  }, [campaignOptions, formData.campaign, formData.campaignId]);
  const mobileRef = useRef(null);
  
  // Custom Country Flag Picker State
  const [allCountries, setAllCountries] = useState([]);
  const [activeCountryPicker, setActiveCountryPicker] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const countryPickerRef = useRef(null);

  // tag input state
  const [tagsArr, setTagsArr] = useState(() => {
    try {
      const raw = String(formData.tags || '');
      return raw.split(',').map(s => s.trim()).filter(Boolean);
    } catch (e) { return []; }
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    let mounted = true;
    const fetchCountries = async () => {
      try {
        const list = await getCountryList({});
        if (!mounted) return;
        setCountries(Array.isArray(list) ? list : []);
      } catch (err) {
        console.warn('fetchCountries error', err);
      }
    };
    fetchCountries();

    const fetchSourceMediumCampaign = async () => {
      try {
        const { TokenId, userId, parentId } = getSession();
        const makePayload = (type, parentid = 0) => ({
          Token: TokenId,
          Message: "",
          LoggedUserId: userId ?? 0,
          MAC_Address: "",
          IP_Address: "102.16.32.189",
          Details: { Type: type, Parentid: parentid },
          BroadcastName: ""
        });

        const sources = await bindSourceMediumCampaign(makePayload('SourceName', parentId || 0));
        const mediums = await bindSourceMediumCampaign(makePayload('MediumName', parentId || 0));
        const campaigns = await bindSourceMediumCampaign(makePayload('CampaignName', parentId || 0));
        
        if (!mounted) return;
        setSourceOptions(Array.isArray(sources) ? sources : []);
        setMediumOptions(Array.isArray(mediums) ? mediums : []);
        setCampaignOptions(Array.isArray(campaigns) ? campaigns : []);
      } catch (err) {
        console.warn('fetchSourceMediumCampaign error', err);
      }
    };
    fetchSourceMediumCampaign();

    const fetchCustomFlags = async () => {
      try {
        const flagsAndCodes = await fetchCountryFlagsAndCodes();
        if (!mounted) return;
        setAllCountries(flagsAndCodes || []);
      } catch (err) {
        console.warn('fetchCustomFlags error', err);
      }
    };
    fetchCustomFlags();

    const handleClickOutside = (e) => {
      if (countryPickerRef.current && !countryPickerRef.current.contains(e.target)) {
        setActiveCountryPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => { 
      mounted = false; 
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  // sync tagsArr -> formData.tags
  useEffect(() => {
    setFormData(prev => ({ ...prev, tags: Array.isArray(tagsArr) ? tagsArr.join(',') : '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsArr]);

  const addTagsFromString = (str) => {
    if (!str) return;
    const parts = String(str).split(',').map(s => s.trim()).filter(Boolean);
    if (!parts.length) return;
    setTagsArr(prev => {
      const set = new Set(prev.map(p => p.toLowerCase()));
      const merged = [...prev];
      parts.forEach(p => {
        if (!set.has(p.toLowerCase())) {
          merged.push(p);
          set.add(p.toLowerCase());
        }
      });
      return merged;
    });
    setTagInput('');
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
      e.preventDefault();
      addTagsFromString(tagInput || '');
    }
  };

  const handleTagPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text') || '';
    if (text && text.includes(',')) {
      e.preventDefault();
      addTagsFromString(text);
    }
  };

  // When countries or countryId changes, try to resolve a dial code from available intl data
  useEffect(() => {
    try {
      if (!allCountries || allCountries.length === 0) return;
      const found = countries.find(c => String(c.Id ?? c.id ?? '') === String(formData.countryId));
      if (found) {
        const iso = (found.Code || found.code || found.Iso2 || found.iso2 || '') ? (found.Code || found.code || found.Iso2 || found.iso2 || '').toString().toLowerCase() : '';
        let cc = null;
        if (iso) {
          cc = allCountries.find(o => o.code.toLowerCase() === iso);
        }
        if (!cc) {
          const name = (found.Text ?? found.text ?? found.Name ?? found.CountryName ?? '').toString().trim().toLowerCase();
          cc = allCountries.find(o => o.name.toLowerCase() === name);
        }
        if (cc) setFormData(prev => ({ ...prev, countryCode: cc.dialCode }));
      }
    } catch (err) {
      console.warn('sync country code error', err);
    }
  }, [countries, formData.countryId, allCountries]);

  // when countries load and a lead has a country name, try to resolve its Id and load states
  useEffect(() => {
    const resolveCountryAndFetchStates = async () => {
      try {
        if (!countries || countries.length === 0) return;
        // if formData already has countryId, fetch states
        if (formData.countryId) {
          const st = await getStateListByCountryId({ Details: { CountryId: formData.countryId } });
          setStates(Array.isArray(st) ? st : []);
          return;
        }

        // try to find matching country by name
        if (formData.country) {
          const found = countries.find(c => {
            const txt = ((c.Text ?? c.text ?? c.Name ?? c.CountryName) || '').toString().trim().toLowerCase();
            return txt === (formData.country ?? '').toString().trim().toLowerCase();
          });
          if (found) {
            setFormData(prev => ({ ...prev, countryId: found.Id ?? found.id ?? '' }));
            const st = await getStateListByCountryId({ Details: { CountryId: found.Id ?? found.id ?? 0 } });
            setStates(Array.isArray(st) ? st : []);
          }
        }
      } catch (err) {
        console.warn('resolveCountryAndFetchStates error', err);
      }
    };
    resolveCountryAndFetchStates();
  }, [countries]);

  // when states load, try to resolve an existing formData.state to Id and fetch cities
  useEffect(() => {
    const resolveStateAndFetchCities = async () => {
      try {
        if (!states || states.length === 0) return;
        if (formData.stateId) {
          const ct = await getCityListByStateId({ Details: { StateId: formData.stateId } });
          setCities(Array.isArray(ct) ? ct : []);
          return;
        }

        if (formData.state) {
          const found = states.find(s => {
            const left = ((s.Text ?? s.text ?? '')).toString().trim().toLowerCase();
            const right = (formData.state ?? '').toString().trim().toLowerCase();
            return left === right;
          });
          if (found) {
            setFormData(prev => ({ ...prev, stateId: found.Id ?? found.id ?? '' }));
            const ct = await getCityListByStateId({ Details: { StateId: found.Id ?? found.id ?? 0 } });
            setCities(Array.isArray(ct) ? ct : []);
          }
        }
      } catch (err) {
        console.warn('resolveStateAndFetchCities error', err);
      }
    };
    resolveStateAndFetchCities();
  }, [states]);

  const handleCountryChange = async (e) => {
    const val = e.target.value;
    // val is country Id (string) or empty
    const countryId = val ? (isNaN(val) ? val : Number(val)) : '';
    const found = countries.find(c => String(c.Id ?? c.id ?? '') === String(countryId));
    const countryText = found ? (found.Text ?? found.text ?? found.Name ?? found.CountryName ?? '') : '';
    setFormData(prev => ({ ...prev, country: countryText, countryId }));
    // fetch states for this country
    if (countryId) {
      try {
        const st = await getStateListByCountryId({ Details: { CountryId: countryId } });
        setStates(Array.isArray(st) ? st : []);
      } catch (err) {
        console.warn('fetch states error', err);
        setStates([]);
      }
    } else {
      setStates([]);
    }
  };

  const handleSourceChange = (e) => {
    const val = e.target.value;
    const found = sourceOptions.find(s => String(s.SrcmedCampID ?? s.SrcmedCampId ?? s.SrcMedCampID ?? s.SrcmedCampID) === String(val));
    const name = found ? (found.SrcmedCampName ?? found.Name ?? '') : '';
    setFormData(prev => ({ ...prev, source: name, sourceId: val }));
  };

  const handleMediumChange = (e) => {
    const val = e.target.value;
    const found = mediumOptions.find(s => String(s.SrcmedCampID ?? s.SrcmedCampId ?? s.SrcMedCampID ?? s.SrcmedCampID) === String(val));
    const name = found ? (found.SrcmedCampName ?? found.Name ?? '') : '';
    setFormData(prev => ({ ...prev, medium: name, mediumId: val }));
  };

  const handleCampaignChange = (e) => {
    const val = e.target.value;
    const found = campaignOptions.find(s => String(s.SrcmedCampID ?? s.SrcmedCampId ?? s.SrcMedCampID ?? s.SrcmedCampID) === String(val));
    const name = found ? (found.SrcmedCampName ?? found.Name ?? '') : '';
    setFormData(prev => ({ ...prev, campaign: name, campaignId: val }));
  };

  const handleStateChange = async (e) => {
    const val = e.target.value;
    const stateId = val ? (isNaN(val) ? val : Number(val)) : '';
    const found = states.find(s => String(s.Id ?? s.id ?? '') === String(stateId));
    const stateText = found ? (found.Text ?? found.text ?? '') : '';
    setFormData(prev => ({ ...prev, state: stateText, stateId }));
    if (stateId) {
      try {
        const ct = await getCityListByStateId({ Details: { StateId: stateId } });
        setCities(Array.isArray(ct) ? ct : []);
      } catch (err) {
        console.warn('fetch cities error', err);
        setCities([]);
      }
    } else {
      setCities([]);
    }
  };

  // ─── Collaborator handlers ──────────────────────────────────────────────────
  useEffect(() => {
    if (!showCollaborator) return;
    let alive = true;
    const { TokenId, userId } = getSession();

    // Fetch User List
    if (userOptions.length === 0) {
      getUserListByUserId({
        Token: TokenId,
        Message: '',
        LoggedUserId: userId ?? 0,
        MAC_Address: '',
        IP_Address: '',
        Details: { UserId: userId || 0 },
        BroadcastName: '',
      }).then(users => {
        if (!alive) return;
        setUserOptions(
          Array.isArray(users)
            ? users.map((u) => ({
              Code: String(u?.Code ?? u?.code ?? u?.UserId ?? u?.userId ?? u?.Id ?? u?.id ?? u?.ID ?? u?.Value ?? u?.value ?? ''),
              Text: u?.Text ?? u?.text ?? u?.UserName ?? u?.userName ?? u?.Name ?? u?.name ?? '',
            })).filter((u) => u.Code || u.Text)
            : []
        );
      }).catch(err => console.warn('EditLeadForm getUserListByUserId fetch error', err));
    }

    // Fetch Collaborator Types
    if (collabTypeOptions.length === 0) {
      getCollaboratorTypes({
        Token: TokenId, Message: '', LoggedUserId: userId ?? 0,
        MAC_Address: '', IP_Address: '',
        Details: { UserID: userId || 0, ClNmae: '' },
        BroadcastName: '',
      }).then(list => {
        if (!alive) return;
        setCollabTypeOptions(
          Array.isArray(list)
            ? list.map(t => ({
              id: String(t?.Id ?? t?.id ?? t?.Code ?? t?.code ?? ''),
              name: t?.TypeName ?? t?.type_name ?? t?.typeName ?? t?.Text ?? t?.text ?? t?.Name ?? t?.name ?? '',
            })).filter(t => t.id || t.name)
            : []
        );
      }).catch(err => console.warn('collabType fetch error', err));
    }

    return () => { alive = false; };
  }, [showCollaborator]);

  useEffect(() => {
    if (!showCollaborator || collabMode !== 'team' || !collabSelectedType.id) {
      setCollabTeamOptions([]);
      return;
    }
    const { TokenId, userId } = getSession();
    getTeamsByTypeId({
      Token: TokenId, Message: '', LoggedUserId: userId ?? 0,
      MAC_Address: '', IP_Address: '',
      Details: { UserID: userId || 0, CollTeamName: '' },
      BroadcastName: '',
    }).then(list => {
      setCollabTeamOptions(
        Array.isArray(list)
          ? list.map(t => ({
            id: String(t?.Id ?? t?.id ?? t?.Code ?? t?.code ?? ''),
            name: t?.TeamName ?? t?.team_name ?? t?.teamName ?? t?.Text ?? t?.text ?? t?.Name ?? t?.name ?? '',
          })).filter(t => t.id || t.name)
          : []
      );
    }).catch(err => console.warn('collabTeam fetch error', err));
  }, [showCollaborator, collabMode, collabSelectedType.id]);

  const handleAddCollaborator = () => {
    if (!collabSelectedType.id || !collabSelectedValue.id) {
      Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Please select both type and team/user.' });
      return;
    }

    const typeName = collabSelectedType.name || collabTypeOptions.find(t => t.id === collabSelectedType.id)?.name || '';
    const selectedName = collabSelectedValue.name ||
      (collabMode === 'team'
        ? collabTeamOptions.find(t => t.id === collabSelectedValue.id)?.name
        : userOptions.find(u => u.Code === collabSelectedValue.id)?.Text) || '';

    const newCollab = {
      typeId: collabSelectedType.id,
      typeName,
      mode: collabMode,
      selectedId: collabSelectedValue.id,
      selectedName,
    };

    const duplicate = collaborators.find(c => c.typeId === newCollab.typeId && c.mode === newCollab.mode && c.selectedId === newCollab.selectedId);
    if (duplicate) {
      Swal.fire({ icon: 'warning', title: 'Duplicate', text: 'This collaborator is already added.' });
      return;
    }

    setCollaborators(prev => [...prev, newCollab]);
    setCollabSelectedValue({ id: '', name: '' });
  };

  const handleRemoveCollaborator = (idx) => {
    setCollaborators(prev => prev.filter((_, i) => i !== idx));
  };
  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <form onSubmit={handleSubmit}>
        {/* Basic Details */}
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-700 mb-4 pb-3 border-b">Basic Details</h3>

          <div className="flex items-start gap-6 mb-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center relative">
                <button type="button" onClick={openMediaLibrary} className="w-full h-full p-0 m-0">
                  <img
                    src={formData.photoUrl || lead?.PhotoUrl || 'https://kit19.com/assets/custom/img/img_avatar.png'}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                </button>
                <button type="button" onClick={openMediaLibrary} className="absolute right-0 bottom-0 p-1.5 bg-white rounded-full text-gray-600 shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 11l6-6 3 3-6 6H9v-3z" /></svg>
                </button>
              </div>
            </div>

            {/* Form Fields */}
            <div className="flex-1 space-y-4">
              {/* Name */}
              <div className='relative'>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter name"
                />
                {formData.name && (
                  <button
                    type="button"
                    onClick={() => handleSaveField('name')}
                    className="absolute right-2 top-[70%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                    title="Save name"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Company */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter company name"
                />
                {formData.company && (
                  <button
                    type="button"
                    onClick={() => handleSaveField('company')}
                    className="absolute right-2 top-[70%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                    title="Save company"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Mobile — Custom React Picker */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                <div className={`relative flex border border-gray-300 rounded focus-within:ring-2 focus-within:ring-blue-500 bg-white`}>
                  <div className="relative border-r border-gray-300 bg-gray-50 flex items-center shrink-0" ref={activeCountryPicker ? countryPickerRef : null}>
                    <div 
                      className="flex items-center px-2 py-2 cursor-pointer h-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveCountryPicker(!activeCountryPicker);
                        setCountrySearchTerm('');
                      }}
                    >
                      {formData.countryCode ? (
                        allCountries.find(c => c.dialCode === formData.countryCode) ? (
                          <>
                            <img src={allCountries.find(c => c.dialCode === formData.countryCode).flag} alt="" className="w-5 h-4 object-cover mr-1" />
                            <span className="text-sm">{formData.countryCode}</span>
                          </>
                        ) : (
                          <span className="text-sm">{formData.countryCode}</span>
                        )
                      ) : (
                        <>
                          <img src="https://flagcdn.com/w20/in.png" alt="IN" className="w-5 h-4 object-cover mr-1" />
                          <span className="text-sm">+91</span>
                        </>
                      )}
                      <ChevronDown size={14} className="ml-1 text-gray-500" />
                    </div>
                    {activeCountryPicker && (
                      <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded shadow-lg z-50 max-h-60 flex flex-col">
                        <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                          <input
                            type="text"
                            className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Search country..."
                            value={countrySearchTerm}
                            onChange={(e) => setCountrySearchTerm(e.target.value)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="overflow-y-auto overflow-x-hidden">
                          {allCountries
                            .filter(c => 
                              c.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) || 
                              c.dialCode.includes(countrySearchTerm)
                            )
                            .map((country, cIdx) => (
                              <div 
                                key={`${country.code}-${cIdx}`} 
                                className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, countryCode: country.dialCode }));
                                  setActiveCountryPicker(false);
                                  setCountrySearchTerm('');
                                }}
                              >
                                <img src={country.flag} alt="" className="w-5 h-4 object-cover mr-2 shrink-0" />
                                <span className="text-sm text-gray-700 whitespace-nowrap truncate">{country.name} ({country.dialCode})</span>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={(e) => {
                      let val = e.target.value.replace(/\D/g, '');
                      // Basic length limits based on selected code
                      const code = formData.countryCode || '+91';
                      let maxLen = 15;
                      if (code === '+91') maxLen = 10;
                      else if (code === '+1') maxLen = 10;
                      else if (code === '+44') maxLen = 10;
                      else if (code === '+971') maxLen = 9;
                      else if (code === '+61') maxLen = 9;
                      
                      if (val.length > maxLen) {
                        val = val.slice(0, maxLen);
                      }
                      setFormData(prev => ({ ...prev, mobile: val }));
                    }}
                    className="flex-1 w-full bg-transparent px-3 py-2 text-sm focus:outline-none"
                    placeholder="Enter mobile number"
                    style={{ paddingRight: '36px' }}
                  />
                  {formData.mobile && (
                    <button
                      type="button"
                      onClick={() => handleSaveField('mobile')}
                      className="absolute right-2 top-[50%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition z-10"
                      title="Save mobile"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Email1 */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email1</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter email"
                />
                {formData.email && (
                  <button
                    type="button"
                    onClick={() => handleSaveField('email')}
                    className="absolute right-2 top-[70%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                    title="Save email"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <div className="relative">
                  <select
                    name="country"
                    value={formData.countryId || ''}
                    onChange={handleCountryChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select Country</option>
                    {countries && countries.length > 0 ? (
                      countries.map((c) => (
                        <option key={c.Id ?? c.id ?? c.Code ?? (c.Code ?? Math.random())} value={c.Id ?? c.id ?? ''}>
                          {c.Text ?? c.text ?? c.Name ?? c.CountryName}
                        </option>
                      ))
                    ) : (
                      <option value="">Loading...</option>
                    )}
                  </select>
                  {formData.country && (
                    <button
                      type="button"
                      onClick={() => handleSaveField('country')}
                      className="absolute right-2 top-[50%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                      title="Save country"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* State and City */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <div className="relative">
                    <select
                      name="state"
                      value={formData.stateId || ''}
                      onChange={handleStateChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select State</option>
                      {states && states.length > 0 ? (
                        states.map((s) => (
                          <option key={s.Id ?? s.id ?? s.Text ?? s.text} value={s.Id ?? s.id ?? ''}>
                            {s.Text ?? s.text ?? ''}
                          </option>
                        ))
                      ) : (
                        <option value="">No states</option>
                      )}
                    </select>
                    {formData.state && (
                      <button
                        type="button"
                        onClick={() => handleSaveField('state')}
                        className="absolute right-2 top-[50%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                        title="Save state"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <div className="relative">
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select City</option>
                      {cities && cities.length > 0 ? (
                        cities.map((c) => (
                          <option key={c.Id ?? c.id ?? c.Text ?? c.text} value={c.Text ?? c.text ?? ''}>
                            {c.Text ?? c.text ?? ''}
                          </option>
                        ))
                      ) : (
                        <option value="">No cities</option>
                      )}
                    </select>
                    {formData.city && (
                      <button
                        type="button"
                        onClick={() => handleSaveField('city')}
                        className="absolute right-2 top-[50%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                        title="Save city"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Other Details */}
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-700 mb-4 pb-3 border-b">Other Details</h3>

          {/* lead ID */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">lead ID :</span>
              <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium">
                {lead?.ID || 'New'}
              </span>
            </div>
          </div>

          {/* Source and Medium */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <div className="relative">
                <select
                  name="source"
                  value={formData.sourceId ?? ''}
                  onChange={handleSourceChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select Source</option>
                  {sourceOptions && sourceOptions.length > 0 ? (
                    sourceOptions.map(opt => (
                      <option key={opt.SrcmedCampID ?? opt.SrcmedCampId ?? opt.SrcMedCampID ?? opt.SrcmedCampID} value={opt.SrcmedCampID ?? opt.SrcmedCampId ?? opt.SrcMedCampID ?? opt.SrcmedCampID}>
                        {opt.SrcmedCampName ?? opt.Name}
                      </option>
                    ))
                  ) : (
                    <option value="">No sources</option>
                  )}
                </select>
                {formData.source && (
                  <button
                    type="button"
                    onClick={() => handleSaveField('source')}
                    className="absolute right-2 top-[50%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                    title="Save source"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Medium</label>
              <div className="relative">
                <select
                  name="medium"
                  value={formData.mediumId ?? ''}
                  onChange={handleMediumChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select Medium</option>
                  {mediumOptions && mediumOptions.length > 0 ? (
                    mediumOptions.map(opt => (
                      <option key={opt.SrcmedCampID ?? opt.SrcmedCampId ?? opt.SrcMedCampID ?? opt.SrcmedCampID} value={opt.SrcmedCampID ?? opt.SrcmedCampId ?? opt.SrcMedCampID ?? opt.SrcmedCampID}>
                        {opt.SrcmedCampName ?? opt.Name}
                      </option>
                    ))
                  ) : (
                    <option value="">No mediums</option>
                  )}
                </select>
                {formData.medium && (
                  <button
                    type="button"
                    onClick={() => handleSaveField('medium')}
                    className="absolute right-2 top-[50%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                    title="Save medium"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Campaign */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
            <div className="relative">
              <select
                name="campaign"
                value={formData.campaignId ?? ''}
                onChange={handleCampaignChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select Campaign</option>
                {campaignOptions && campaignOptions.length > 0 ? (
                  campaignOptions.map(opt => (
                    <option key={opt.SrcmedCampID ?? opt.SrcmedCampId ?? opt.SrcMedCampID ?? opt.SrcmedCampID} value={opt.SrcmedCampID ?? opt.SrcmedCampId ?? opt.SrcMedCampID ?? opt.SrcmedCampID}>
                      {opt.SrcmedCampName ?? opt.Name}
                    </option>
                  ))
                ) : (
                  <option value="">No campaigns</option>
                )}
              </select>
              {formData.campaign && (
                <button
                  type="button"
                  onClick={() => handleSaveField('campaign')}
                  className="absolute right-2 top-[50%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                  title="Save campaign"
                >
                  <Save className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Resi. Address and Office Address */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resi. Address</label>
              <div className="relative">
                <textarea
                  name="resiAddress"
                  value={formData.resiAddress}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                  placeholder="Address"
                />
                {formData.resiAddress && (
                  <button
                    type="button"
                    onClick={() => handleSaveField('resiAddress')}
                    className="absolute right-2 top-2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                    title="Save resi address"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Office Address</label>
              <div className="relative">
                <textarea
                  name="officeAddress"
                  value={formData.officeAddress}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                  placeholder="Address"
                />
                {formData.officeAddress && (
                  <button
                    type="button"
                    onClick={() => handleSaveField('officeAddress')}
                    className="absolute right-2 top-2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                    title="Save office address"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Remark */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Remark</label>
            <div className="relative">
              <textarea
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                placeholder="Enter remarks"
              />
              {formData.remark && (
                <button
                  type="button"
                  onClick={() => handleSaveField('remark')}
                  className="absolute right-2 top-2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                  title="Save remark"
                >
                  <Save className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Pin code */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Pin code</label>
            <div className="relative">
              <input
                type="text"
                name="pinCode"
                value={formData.pinCode}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Enter pin code"
              />
              {formData.pinCode && (
                <button
                  type="button"
                  onClick={() => handleSaveField('pinCode')}
                  className="absolute right-2 top-[50%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                  title="Save pin code"
                >
                  <Save className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* lead Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">lead Tags</label>
            <div className="relative">
              <div className="flex items-center gap-2 flex-wrap p-2 border border-gray-300 rounded-md mb-2">
                {tagsArr.map((t, idx) => (
                  <span key={`${t}-${idx}`} className="flex items-center gap-2 bg-gray-100 text-sm text-gray-800 px-2 py-1 rounded-full">
                    <span>{t}</span>
                    <button type="button" onClick={() => setTagsArr(prev => prev.filter((_, i) => i !== idx))} className="text-gray-500 hover:text-gray-700">×</button>
                  </span>
                ))}

                <input
                  type="text"
                  name="tags_input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onPaste={handleTagPaste}
                  className="flex-1 min-w-[160px] px-2 py-1 text-sm bg-transparent border-0 focus:outline-none"
                  placeholder="Search tags"
                />
              </div>
              {(tagsArr.length > 0) && (
                <button
                  type="button"
                  onClick={() => handleSaveField('tags')}
                  className="absolute right-2 top-[50%] -translate-y-1/2 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                  title="Save tags"
                >
                  <Save className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="bg-green-50 border border-green-200 rounded-md px-3 py-2 text-xs text-green-700">
              Please press comma, tab, or enter key for generating the tag name, or you can also generate a tag by copy and paste with comma separated value.
            </div>
          </div>

          {/* ── Collaborator section ──────────────────────────────────────────── */}
          <div className="drawer-section premium-card mt-4">
            <div className="drawer-section-header">
              Collaborator
            </div>
            <div className="drawer-section-body">
              <label className="flex items-center gap-2 cursor-pointer select-none mb-3">
                <input
                  type="checkbox"
                  checked={showCollaborator}
                  onChange={e => {
                    setShowCollaborator(e.target.checked);
                    if (!e.target.checked) {
                      setCollaborators([]);
                      setCollabSelectedType({ id: '', name: '' });
                      setCollabSelectedValue({ id: '', name: '' });
                      setCollabMode('team');
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Add Collaborator</span>
              </label>

              {showCollaborator && (
                <div className="p-3 border border-gray-200 rounded-md bg-gray-50">
                  {/* Controls row */}
                  <div className="flex flex-wrap gap-3 items-start mb-3">
                    {/* Type dropdown */}
                    <select
                      value={collabSelectedType.id}
                      onChange={e => {
                        const found = collabTypeOptions.find(t => t.id === e.target.value);
                        setCollabSelectedType(found ?? { id: e.target.value, name: '' });
                        setCollabSelectedValue({ id: '', name: '' });
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
                    >
                      <option value="">-- Select Type --</option>
                      {collabTypeOptions.map((t, i) => (
                        <option key={i} value={t.id}>{t.name}</option>
                      ))}
                    </select>

                    {/* Radio: Team / User */}
                    <div className="flex gap-4 items-center">
                      <label className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="collabAddSelectMode"
                          value="team"
                          checked={collabMode === 'team'}
                          onChange={() => {
                            setCollabMode('team');
                            setCollabSelectedValue({ id: '', name: '' });
                          }}
                          className="text-blue-600"
                        />
                        Select Team
                      </label>
                      <label className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="collabAddSelectMode"
                          value="user"
                          checked={collabMode === 'user'}
                          onChange={() => {
                            setCollabMode('user');
                            setCollabSelectedValue({ id: '', name: '' });
                          }}
                          className="text-blue-600"
                        />
                        Select User
                      </label>
                    </div>

                    {/* Team or User dropdown */}
                    {collabMode === 'team' ? (
                      <select
                        value={collabSelectedValue.id}
                        onChange={e => {
                          const found = collabTeamOptions.find(t => t.id === e.target.value);
                          setCollabSelectedValue(found ?? { id: e.target.value, name: '' });
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
                      >
                        <option value="">-- Select Team --</option>
                        {collabTeamOptions.map((t, i) => (
                          <option key={i} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={collabSelectedValue.id}
                        onChange={e => {
                          const found = userOptions.find(u => u.Code === e.target.value);
                          setCollabSelectedValue({ id: e.target.value, name: found?.Text ?? e.target.value });
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
                      >
                        <option value="">-- Select User --</option>
                        {userOptions.map((u, i) => (
                          <option key={i} value={u.Code}>{u.Text}</option>
                        ))}
                      </select>
                    )}

                    {/* Add button */}
                    <button
                      type="button"
                      onClick={handleAddCollaborator}
                      disabled={!collabSelectedType.id || !collabSelectedValue.id}
                      className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add
                    </button>
                  </div>

                  {/* Collaborators table */}
                  {collaborators.length > 0 && (
                    <table className="w-full text-sm border border-gray-200 rounded-md overflow-hidden">
                      <thead className="bg-gray-100">
                        <tr>
                          {['TYPE', 'MODE', 'SELECTED', 'ACTION'].map(h => (
                            <th key={h} className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200 text-xs">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {collaborators.map((c, i) => (
                          <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-white">
                            <td className="px-3 py-2 text-gray-700">{c.typeName || c.typeId}</td>
                            <td className="px-3 py-2 text-gray-700 capitalize">{c.mode}</td>
                            <td className="px-3 py-2 text-gray-700">{c.selectedName || c.selectedId}</td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => handleRemoveCollaborator(i)}
                                className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500 text-white hover:bg-red-600"
                              >
                                <Minus className="w-3 h-3" /> Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {showMediaLibrary && (
          <MediaLibrary
            show={showMediaLibrary}
            onclose={() => setShowMediaLibrary(false)}
            onSelectMedia={handleMediaSelect}
            filterOption="Images"
          />
        )}
      </form>
    </div>
  );
});

export default EditLeadForm;
